import React from "react";
import "../styles/StatsCard.scss";
import {
  formatAvgDuration,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTime,
} from "../helpers/numbers.helper";

export type StatCardVariant =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "purple"
  | "teal"
  | "slate";

interface StatCardProps {
  title: string;
  value: number;
  change?: string;
  changeLabel?: string;
  trend?: "up" | "down";
  icon: string;
  variant: StatCardVariant;
  description: string;
  module?: string | any;
  valueFormat?: string | any;
  genderBreakdown?: {
    male: number;
    female: number;
    other: number;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeLabel = "vs last period",
  trend,
  icon,
  variant,
  description,
  module,
  valueFormat,
  genderBreakdown
}) => {
  const fetchValue = (value: any) => {
    switch (valueFormat) {
      case "duration":
        return formatDuration(value);
      case "avg-duration":
        return formatAvgDuration(value);
      case "number":
        return formatNumber(value);
      case "percentage":
        return formatPercentage(value);
      case "time":
        return formatTime(value);
      default:
        return value;
    }
  };

  // Render
  return (
    <div className={`stat-card stat-card--${variant}`}>
      <div className="card-decoration"></div>

      <div className="stat-content">
        <div className="stat-header">
          <div className="icon-container">
            <i className={`ti ${icon}`}></i>
          </div>
          <h3 className="stat-title">{title}</h3>

          <div className="info-icon-container">
            <i className="info-icon ti ti-info-circle text-muted" />
            <div className="tooltip-text">{description}</div>
          </div>
        </div>

        <div className="stat-body">
          <div className="stat-value">{fetchValue(value)}</div>
          {valueFormat == "number" && (
            <div className="full-value-tooltip">{value.toLocaleString()}</div>
          )}

          {change && trend && module && module == "assets" ? (
            <div className={`stat-change ${trend}`}>
              <div className="change-badge">
                <span className="trend-icon">
                  <span className={`change-value ms-1 text-info `}>
                    {title != "Total Assets" ? change : "0.00"}
                  </span>
                  <span className="change-label ms-1">
                    {title != "Total Assets" ? "of total assets" : ""}
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <div className={`stat-change ${trend}`}>
              <div className="change-badge">
                <span className="trend-icon">
                  <i
                    className={`ti ${trend === "up"
                      ? "ti-arrow-wave-right-up text-success"
                      : "ti-arrow-wave-right-down text-danger"
                      }`}
                  />
                  <span
                    className={`change-value ms-1 ${trend == "up" ? "text-success" : "text-danger"
                      }`}>
                    {change}
                  </span>
                  <span className="change-label ms-1">{changeLabel}</span>
                </span>
              </div>
            </div>
          )}
          {genderBreakdown && (
            <div className="gender-breakdown mt-3">
              <div className="d-flex justify-content-around text-center">
                <div className="gender-item">
                  <div className="gender-icon">
                    <i className="ti ti-man text-info"></i>
                  </div>
                  <div className="gender-value fw-semibold">
                    {genderBreakdown.male}
                  </div>
                  <div className="gender-label text-muted small">Male</div>
                </div>

                <div className="gender-item">
                  <div className="gender-icon">
                    <i className="ti ti-woman text-purple"></i>
                  </div>
                  <div className="gender-value fw-semibold">
                    {genderBreakdown.female}
                  </div>
                  <div className="gender-label text-muted small">Female</div>
                </div>

                <div className="gender-item">
                  <div className="gender-icon">
                    <i className="ti ti-user-question text-slate"></i>
                  </div>
                  <div className="gender-value fw-semibold">
                    {genderBreakdown.other}
                  </div>
                  <div className="gender-label text-muted small">Other</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-decoration-bottom"></div>
    </div>
  );
};

export default StatCard;
